import type { TFunction } from 'i18next';
import type { AudioDeviceState } from 'utils/audio/audioDeviceTypes';

export type TroubleshootingBlockKey =
  'jabraMissing' | 'jabraSilent' | 'inaccessible' | 'accessBlocked' | 'noDevice';

export interface TroubleshootingStep {
  title: string;
  description: string;
}

export interface TroubleshootingBlock {
  heading: string;
  steps: TroubleshootingStep[];
}

// Device-specific steps exist only for the Jabra Speak family on purpose. The recording dialog shows
// other microphones the support hint alone; the settings card falls back to the Jabra steps.
const STEP_KEYS: Record<TroubleshootingBlockKey, string[]> = {
  // A missing Jabra is a connection problem; a connected but silent one starts at the mute button.
  jabraMissing: ['jabra.powerOn', 'jabra.usb', 'jabra.windowsDetection', 'jabra.windowsTest'],
  jabraSilent: ['jabra.mute', 'jabra.usb', 'jabra.windowsTest'],
  inaccessible: ['inaccessible.closeOtherApps', 'jabra.usb', 'jabra.windowsDetection'],
  accessBlocked: ['accessBlocked.openSettings', 'accessBlocked.allowApps', 'accessBlocked.restart'],
  noDevice: ['noDevice.connectJabra', 'noDevice.checkPrivacy'],
};

export function blockKeyForState(state: AudioDeviceState): TroubleshootingBlockKey | null {
  switch (state) {
    case 'recommendedConnected':
      return 'jabraSilent';
    case 'recommendedMissing':
    case 'recommendedNeverSeen':
      return 'jabraMissing';
    case 'recommendedInaccessible':
      return 'inaccessible';
    case 'accessBlocked':
      return 'accessBlocked';
    case 'noDevice':
      return 'noDevice';
    case 'searching':
    case 'confirmedConnected':
    case 'confirmedMissing':
    case 'confirmedInaccessible':
      return null;
  }
}

export function troubleshootingBlock(
  key: TroubleshootingBlockKey,
  t: TFunction
): TroubleshootingBlock {
  return {
    heading: t(
      key === 'accessBlocked'
        ? 'audio.troubleshooting.accessHeading'
        : 'audio.troubleshooting.heading'
    ),
    steps: STEP_KEYS[key].map((stepKey) => ({
      title: t(`audio.troubleshooting.${stepKey}.title`),
      description: t(`audio.troubleshooting.${stepKey}.description`),
    })),
  };
}
